import { Injectable, NgZone } from '@angular/core';
import { NgxSpinnerService, Spinner, PRIMARY_SPINNER } from 'ngx-spinner';
import { HttpRequest } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class CustomSpinnerService extends NgxSpinnerService {
  // Static state guarantees exact synchronization across DI injectors
  private static activeRequests = new Set<HttpRequest<any>>();
  private static manualShowCount = 0;
  private static hideTimeout: any = null;

  constructor(private ngZone: NgZone) {
    super();
  }

  /**
   * Tracks an incoming HTTP request and forces the spinner to show.
   */
  showHttp(req: HttpRequest<any>): void {
    CustomSpinnerService.activeRequests.add(req);
    
    // Clear any pending debounced hide timeout since a new request has started
    if (CustomSpinnerService.hideTimeout) {
      clearTimeout(CustomSpinnerService.hideTimeout);
      CustomSpinnerService.hideTimeout = null;
    }

    // Call the underlying NgxSpinnerService show method directly to display the spinner inside Angular Zone
    this.ngZone.run(() => {
      super.show(PRIMARY_SPINNER).then(() => this.hoistSpinnerAboveDialogs());
    });
  }

  /**
   * Removes a request from the active set early (e.g. when response/error is received)
   * to allow manual hides in component callbacks to succeed immediately.
   */
  removeActiveRequest(req: HttpRequest<any>): void {
    CustomSpinnerService.activeRequests.delete(req);
  }

  /**
   * Tracks completion of an HTTP request.
   * Debounces the actual hiding of the spinner to prevent flickering.
   */
  hideHttp(req: HttpRequest<any>, routerUrl: string, requestUrl: string): void {
    CustomSpinnerService.activeRequests.delete(req);
    this.checkAndScheduleHide(routerUrl, requestUrl);
  }

  /**
   * Overrides the standard show() method of NgxSpinnerService (called manually by components).
   */
  override show(name: string = PRIMARY_SPINNER, spinner?: Spinner): Promise<any> {
    CustomSpinnerService.manualShowCount++;
    
    if (CustomSpinnerService.hideTimeout) {
      clearTimeout(CustomSpinnerService.hideTimeout);
      CustomSpinnerService.hideTimeout = null;
    }

    return this.ngZone.run(() => {
      return Promise.resolve(super.show(name, spinner)).then((result) => {
        this.hoistSpinnerAboveDialogs();
        return result;
      });
    });
  }

  /**
   * Overrides the standard hide() method of NgxSpinnerService (called manually by components).
   * It will only delegate to the actual hide() if no active HTTP requests remain.
   */
  override hide(name: string = PRIMARY_SPINNER, debounce: number = 10): Promise<any> {
    if (CustomSpinnerService.manualShowCount > 0) {
      CustomSpinnerService.manualShowCount--;
    }

    // If there are still active HTTP requests, do not hide!
    if (CustomSpinnerService.activeRequests.size > 0) {
      return Promise.resolve(true);
    }

    this.releaseSpinnerPopover();

    return this.ngZone.run(() => {
      return super.hide(name, debounce);
    });
  }

  /**
   * Evaluates current state and hides the spinner with a 1000ms debounce/delay buffer if appropriate.
   */
  private checkAndScheduleHide(routerUrl: string, requestUrl: string): void {
    // If there are still active requests, do not hide
    if (CustomSpinnerService.activeRequests.size > 0) {
      if (CustomSpinnerService.hideTimeout) {
        clearTimeout(CustomSpinnerService.hideTimeout);
        CustomSpinnerService.hideTimeout = null;
      }
      return;
    }

    // Clear any existing timeout before scheduling a new one
    if (CustomSpinnerService.hideTimeout) {
      clearTimeout(CustomSpinnerService.hideTimeout);
    }


    // Use a 1000ms debounce to prevent flickering between sequential requests
    CustomSpinnerService.hideTimeout = setTimeout(() => {
      CustomSpinnerService.hideTimeout = null;

      // Double check that no new requests started during the debounce period
      if (CustomSpinnerService.activeRequests.size === 0) {
        
        // Preserve all legacy router/request path exclusions
        if (requestUrl.indexOf('completeregistration') <= -1) {
          if (requestUrl.split('/')[5] !== 'folders' && routerUrl.indexOf('/dash/profile') <= -1) {
            this.ngZone.run(() => {
              this.releaseSpinnerPopover();
              super.hide(PRIMARY_SPINNER);
            });
          } else if (requestUrl.split('/')[5] !== 'folders' && requestUrl.indexOf('/task/list') <= -1) {
            setTimeout(() => {
              if (CustomSpinnerService.activeRequests.size === 0) {
                this.ngZone.run(() => {
                  this.releaseSpinnerPopover();
                  super.hide(PRIMARY_SPINNER);
                });
              }
            }, 1000);
          } else {
          }
        } else {
        }

      } else {
      }
    }, 1000);
  }

  /**
   * Angular CDK 21 MatDialog uses the Popover API (browser top layer). ngx-spinner lives in
   * normal document stacking, so it paints under open dialogs. Promote the spinner overlay
   * into the top layer (after the dialog) so loading appears above the dialog.
   */
  private hoistSpinnerAboveDialogs(): void {
    // Overlay is created via *ngIf after show() — wait a tick for it to exist.
    requestAnimationFrame(() => {
      setTimeout(() => {
        const overlay = document.querySelector('.ngx-spinner-overlay') as HTMLElement | null;
        if (!overlay) {
          return;
        }

        const dialogOpen = !!document.querySelector(
          '.cdk-overlay-popover, [popover="manual"].cdk-overlay-popover',
        );
        if (!dialogOpen) {
          return;
        }

        if (!overlay.hasAttribute('popover')) {
          overlay.setAttribute('popover', 'manual');
        }

        // Reset UA popover chrome so it still covers the viewport like a normal overlay.
        overlay.style.setProperty('inset', '0', 'important');
        overlay.style.setProperty('width', '100vw', 'important');
        overlay.style.setProperty('height', '100vh', 'important');
        overlay.style.setProperty('max-width', 'none', 'important');
        overlay.style.setProperty('max-height', 'none', 'important');
        overlay.style.setProperty('margin', '0', 'important');
        overlay.style.setProperty('border', 'none', 'important');
        overlay.style.setProperty('padding', '0', 'important');
        overlay.style.setProperty('overflow', 'visible', 'important');
        overlay.style.setProperty('position', 'fixed', 'important');

        try {
          if (typeof (overlay as any).showPopover === 'function') {
            if (!overlay.matches(':popover-open')) {
              (overlay as any).showPopover();
            }
          }
        } catch {
          // Older browsers without Popover API — leave spinner as-is.
        }
      }, 0);
    });
  }

  private releaseSpinnerPopover(): void {
    const overlay = document.querySelector('.ngx-spinner-overlay') as HTMLElement | null;
    if (!overlay) {
      return;
    }
    try {
      if (
        typeof (overlay as any).hidePopover === 'function' &&
        overlay.matches(':popover-open')
      ) {
        (overlay as any).hidePopover();
      }
    } catch {
      // ignore
    }
  }
}
